import React, { useState, useEffect } from 'react';
import { getRestaurantUsers, createRestaurantUser, updateRestaurantUser, deleteRestaurantUser } from '../../services/api';
import { useNavigate } from 'react-router-dom';

function ManageWaitersScreen() {
  const [waiters, setWaiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ UserID: null, Username: '', FullName: '', Password: '' });
  const navigate = useNavigate();
  const restaurantId = localStorage.getItem('restaurantId'); // Obter o ID do restaurante logado

  useEffect(() => {
    if (!restaurantId) {
      setError('ID do restaurante não encontrado. Faça login novamente.');
      setLoading(false);
      return;
    }
    fetchWaiters();
  }, [restaurantId]);

  const fetchWaiters = async () => {
    setLoading(true);
    setError('');
    try {
      const users = await getRestaurantUsers();
      // Filtrar apenas garçons
      setWaiters(users.filter(user => user.Role === 'waiter'));
    } catch (err) {
      console.error('Failed to fetch waiters:', err);
      setError('Não foi possível carregar os garçons.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!restaurantId) {
      setError('ID do restaurante não encontrado. Faça login novamente.');
      return;
    }

    try {
      if (form.UserID) {
        // Atualizar garçom existente
        await updateRestaurantUser(form.UserID, {
          Username: form.Username,
          FullName: form.FullName,
          ...(form.Password && { Password: form.Password }), // Só envia a senha se for alterada
        });
      } else {
        // Criar novo garçom
        await createRestaurantUser({
          Username: form.Username,
          FullName: form.FullName,
          Password: form.Password,
          Role: 'waiter',
          RestaurantID: restaurantId,
        });
      }
      setForm({ UserID: null, Username: '', FullName: '', Password: '' }); // Limpar formulário
      fetchWaiters(); // Recarregar lista
    } catch (err) {
      console.error('Failed to save waiter:', err);
      setError('Falha ao salvar garçom. Verifique os dados.');
    }
  };

  const handleEdit = (waiter) => {
    setForm({
      UserID: waiter.UserID,
      Username: waiter.Username,
      FullName: waiter.FullName,
      Password: '', // Não pré-preencher a senha por segurança
    });
  };

  const handleDelete = async (userId) => {
    setError('');
    if (window.confirm('Tem certeza que deseja excluir este garçom?')) {
      try {
        await deleteRestaurantUser(userId);
        fetchWaiters();
      } catch (err) {
        console.error('Failed to delete waiter:', err);
        setError('Falha ao excluir garçom.');
      }
    }
  };

  if (loading) {
    return <div style={styles.loading}>Carregando garçons...</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Gerenciar Garçons</h2>
      <button onClick={() => navigate('/restaurant/dashboard')} style={styles.backButton}>
        Voltar ao Painel
      </button>

      <div style={styles.formSection}>
        <h3>{form.UserID ? 'Editar Garçom' : 'Adicionar Novo Garçom'}</h3>
        {error && <p style={styles.error}>{error}</p>}
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            name="Username"
            placeholder="Usuário"
            value={form.Username}
            onChange={handleChange}
            style={styles.input}
            required
          />
          <input
            type="text"
            name="FullName"
            placeholder="Nome Completo"
            value={form.FullName}
            onChange={handleChange}
            style={styles.input}
            required
          />
          <input
            type="password"
            name="Password"
            placeholder={form.UserID ? "Nova Senha (opcional)" : "Senha"}
            value={form.Password}
            onChange={handleChange}
            style={styles.input}
            required={!form.UserID} // Senha é obrigatória apenas para novos usuários
          />
          <button type="submit" style={styles.submitButton}>
            {form.UserID ? 'Salvar Alterações' : 'Adicionar Garçom'}
          </button>
          {form.UserID && (
            <button type="button" onClick={() => setForm({ UserID: null, Username: '', FullName: '', Password: '' })} style={styles.cancelButton}>
              Cancelar Edição
            </button>
          )}
        </form>
      </div>

      <div style={styles.listSection}>
        <h3>Lista de Garçons</h3>
        {waiters.length === 0 ? (
          <p>Nenhum garçom cadastrado.</p>
        ) : (
          <ul style={styles.list}>
            {waiters.map((waiter) => (
              <li key={waiter.UserID} style={styles.listItem}>
                <span>{waiter.FullName} ({waiter.Username})</span>
                <div style={styles.actions}>
                  <button onClick={() => handleEdit(waiter)} style={styles.editButton}>Editar</button>
                  <button onClick={() => handleDelete(waiter.UserID)} style={styles.deleteButton}>Excluir</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f0f2f5',
    minHeight: '100vh',
    fontFamily: 'Arial, sans-serif',
  },
  title: {
    color: '#333',
    marginBottom: '20px',
  },
  backButton: {
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 15px',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  formSection: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '600px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  input: {
    padding: '10px',
    marginBottom: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
  },
  submitButton: {
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 15px',
    cursor: 'pointer',
    marginTop: '10px',
  },
  cancelButton: {
    backgroundColor: '#ffc107',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 15px',
    cursor: 'pointer',
    marginTop: '10px',
  },
  listSection: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    width: '100%',
    maxWidth: '600px',
  },
  list: {
    listStyle: 'none',
    padding: 0,
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #eee',
  },
  actions: {
    display: 'flex',
    gap: '10px',
  },
  editButton: {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
  },
  deleteButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
  },
  loading: {
    fontSize: '18px',
    color: '#555',
    marginTop: '50px',
  },
  error: {
    color: 'red',
    marginBottom: '10px',
    textAlign: 'center',
  },
};

export default ManageWaitersScreen;
